import { useTimeHorizons } from "@/hooks/useTimeHorizons";
import {
    ModelVariable,
    ModelVariableDataType,
    TimeHorizonShape,
} from "@/models";
import { ModalContainer } from "@/modules/shared";
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import { DatabaseIcon, Pencil } from "lucide-react";

import React, { useEffect, useState } from "react";
import { useGetModelBlocksBySimulationId } from "../admin-components/model-variable-selection/useGetModelBlocks";
import { useMutation } from "@tanstack/react-query";
import { sapienAxios } from "@/inertia-utils/hooks";

const saveRichTextModelQuantity = async (
    richTextModelQuantity: App.Data.RichTextModelQuantityData,
) => {
    if (richTextModelQuantity.id) {
        const { data } =
            await sapienAxios.put<App.Data.RichTextModelQuantityData>(
                `creator.design.rich-text-model-quantities.update`,
                {
                    id: richTextModelQuantity.id,
                    model_variable_id: richTextModelQuantity.model_variable_id,
                    time_horizon_id: richTextModelQuantity.time_horizon_id,
                },
                {
                    rich_text_model_quantity: richTextModelQuantity.id,
                },
            );
        return data;
    }
    const { data } = await sapienAxios.post<App.Data.RichTextModelQuantityData>(
        "creator.design.rich-text-model-quantities.store",
        {
            model_variable_id: richTextModelQuantity.model_variable_id,
            time_horizon_id: richTextModelQuantity.time_horizon_id,
        },
    );
    return data;
};

type Props = {
    modelVariableId: string;
    timeHorizonId: string;
    richTextModelQuantityId: string;
    onChange: ({
        richTextModelQuantity,
    }: {
        richTextModelQuantity: App.Data.RichTextModelQuantityData;
    }) => void;
};

export const ModelQuantityInlineButton = ({
    modelVariableId,
    timeHorizonId,
    richTextModelQuantityId,
    onChange,
    triggerClassName,
    triggerContent,
}: Props & {
    // When provided, render a normal in-flow trigger (e.g. inside a popover)
    // instead of the default absolutely-positioned hover pencil.
    triggerClassName?: string;
    triggerContent?: React.ReactNode;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                className={
                    triggerClassName ??
                    `absolute left-1/2 top-1/2 rounded-full bg-white p-1 opacity-0
                    transition-opacity duration-300 -translate-x-1/2 -translate-y-1/2
                    group-hover/inline-variable:opacity-100`
                }
                onClick={() => setIsOpen(true)}
            >
                {triggerContent ?? <Pencil className="h-4 w-4" />}
            </button>
            <ModelQuantityModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                modelVariableId={modelVariableId}
                timeHorizonId={timeHorizonId}
                onChange={onChange}
                richTextModelQuantityId={richTextModelQuantityId}
            />
        </>
    );
};

export default function ModelQuantity({
    modelVariableId,
    timeHorizonId,
    onChange,
}: Props) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                className={`flex items-center justify-center rounded-sm px-2 py-2 text-center capitalize
                hover:border-white/20 hover:bg-sapien-blue hover:text-white focus:outline-none
                focus:ring-opacity-50 ${
                    !!modelVariableId
                        ? "border-gray-300/50 bg-sapien-blue text-white"
                        : "border-gray-300/30 bg-slate-100 text-slate-900"
                }`}
                onClick={() => setIsOpen(true)}
            >
                <DatabaseIcon className="h-4 w-4" />
            </button>
            <ModelQuantityModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                modelVariableId={modelVariableId}
                timeHorizonId={timeHorizonId}
                onChange={onChange}
            />
        </>
    );
}

function ModelQuantityModal({
    isOpen,
    setIsOpen,
    modelVariableId,
    timeHorizonId,
    richTextModelQuantityId,
    onChange,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    modelVariableId: string;
    timeHorizonId?: string;
    richTextModelQuantityId?: string;
    onChange: ({
        richTextModelQuantity,
    }: {
        richTextModelQuantity: App.Data.RichTextModelQuantityData;
    }) => void;
}) {
    const { data: modelBlocks, isPending } = useGetModelBlocksBySimulationId();
    const { timeHorizons, isLoading: isLoadingTimeHorizons } =
        useTimeHorizons();

    const numericalModelVariables = modelBlocks?.reduce((acc, block) => {
        return [
            ...acc,
            ...block.modelVariables.filter(
                (variable) =>
                    variable.data_type === ModelVariableDataType.Number,
            ),
        ];
    }, []);

    useEffect(() => {
        if (numericalModelVariables?.length && modelVariableId) {
            setModelVariable(
                numericalModelVariables.find(
                    (variable) => variable.id === modelVariableId,
                ),
            );
        }
    }, [numericalModelVariables?.length, modelVariableId]);

    useEffect(() => {
        if (
            !!timeHorizons &&
            !Array.isArray(timeHorizons) &&
            Object.values(timeHorizons).length
        ) {
            setTimeHorizon(
                (Object.values(timeHorizons) as TimeHorizonShape[])?.find(
                    (horizon) => horizon.id === timeHorizonId,
                ) || null,
            );
        }

        setTimeHorizon(
            timeHorizons.find((horizon) => horizon.id === timeHorizonId) ||
                null,
        );
    }, [timeHorizonId, timeHorizons]);

    const [query, setQuery] = useState("");

    const [modelVariable, setModelVariable] = useState<ModelVariable | null>(
        null,
    );

    const [timeHorizon, setTimeHorizon] = useState<TimeHorizonShape | null>(
        null,
    );

    const { mutate: createRichTextModelQuantity, isPending: isCreating } =
        useMutation({
            mutationFn: async ({
                modelVariableId,
                timeHorizonId,
                richTextModelQuantityId,
            }: {
                modelVariableId: string;
                timeHorizonId: string;
                richTextModelQuantityId: string;
            }) => {
                const data = await saveRichTextModelQuantity({
                    model_variable_id: modelVariableId,
                    time_horizon_id: timeHorizonId,
                    id: richTextModelQuantityId,
                });
                return data;
            },
            onSuccess: (data) => {
                onChange({
                    richTextModelQuantity: data,
                });
                setIsOpen(false);
            },
        });
    return (
        <ModalContainer
            isModalOpen={isOpen}
            setIsModalOpen={() => setIsOpen(false)}
            size="s"
            classNames="z-[999999999]"
        >
            <div className="flex justify-between">
                <h3 className="w-3/4 text-lg font-bold">
                    Replace text with a quantity from the model
                </h3>
            </div>

            <div className="flex flex-col gap-4 overflow-visible p-4">
                <div className="relative flex flex-col gap-2">
                    <Combobox
                        onChange={(value) => {
                            const modelVariable = numericalModelVariables.find(
                                (variable) => variable.id === value,
                            );
                            setModelVariable(modelVariable);

                            if (!modelVariable?.uses_time) {
                                setTimeHorizon(null);
                            }
                        }}
                    >
                        <label>Model Variable</label>
                        <ComboboxInput
                            className="w-full rounded-md border-gray-300 bg-white p-2 shadow"
                            displayValue={(value) => modelVariable?.label}
                            onChange={(e) => {
                                setQuery(e.target.value);
                            }}
                            placeholder={`${modelVariable?.label || "Search for a model variable"}`}
                        />
                        <ComboboxOptions
                            className="absolute z-100 mt-20 flex max-h-96 w-full flex-col space-y-2 overflow-y-auto
                                rounded-md border-gray-300 bg-white p-2 shadow transition-all duration-300
                                scrollbar scrollbar-thumb-gray-500 scrollbar-thumb-rounded-full scrollbar-w-2
                                focus:text-white focus:outline-offset-0 focus:outline-[#1d4ed8] sm:text-sm"
                        >
                            {modelBlocks
                                ?.filter((block) => {
                                    if (!query?.length) return true;

                                    return block.modelVariables.some(
                                        (variable) => {
                                            return (
                                                variable.label
                                                    .toLowerCase()
                                                    .includes(
                                                        query.toLowerCase(),
                                                    ) &&
                                                numericalModelVariables.some(
                                                    (v) => v.id === variable.id,
                                                )
                                            );
                                        },
                                    );
                                })
                                .map((block) => (
                                    <div key={block.id}>
                                        <h4 className="text-sm font-bold">
                                            {block.label}
                                        </h4>
                                        <div className="flex flex-col gap-1 pl-2">
                                            {block.modelVariables
                                                .filter(
                                                    (variable) =>
                                                        !query ||
                                                        (variable.label
                                                            .toLowerCase()
                                                            .includes(
                                                                query.toLowerCase(),
                                                            ) &&
                                                            numericalModelVariables.some(
                                                                (v) =>
                                                                    v.id ===
                                                                    variable.id,
                                                            )),
                                                )
                                                .map((variable) => (
                                                    <ComboboxOption
                                                        key={variable.id}
                                                        value={variable.id}
                                                    >
                                                        {variable.label}
                                                    </ComboboxOption>
                                                ))}
                                        </div>
                                    </div>
                                ))}
                        </ComboboxOptions>
                    </Combobox>
                </div>
                {modelVariable?.uses_time && (
                    <div className="flex flex-col gap-2">
                        <select
                            className="w-full rounded-md border-gray-300 bg-white p-2 shadow"
                            onChange={(e) =>
                                setTimeHorizon(
                                    timeHorizons.find(
                                        (horizon) =>
                                            horizon.id === e.target.value,
                                    ) || null,
                                )
                            }
                            value={timeHorizon?.id}
                        >
                            <option value="">Select Time Horizon</option>
                            {timeHorizons
                                .sort((a, b) => a.time_index - b.time_index)
                                .map((horizon) => (
                                    <option key={horizon.id} value={horizon.id}>
                                        Time Horizon {horizon.time_index}
                                    </option>
                                ))}
                        </select>
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button
                        disabled={
                            !modelVariable ||
                            (modelVariable.uses_time && !timeHorizon)
                        }
                        onClick={() => {
                            createRichTextModelQuantity({
                                modelVariableId: modelVariable.id,
                                timeHorizonId: timeHorizon?.id,
                                richTextModelQuantityId,
                            });
                            setIsOpen(false);
                        }}
                        className="rounded-md border-gray-300 bg-sapien-blue p-2 text-white shadow"
                    >
                        Save
                    </button>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="rounded-md border-gray-300 bg-red-500 p-2 text-white shadow"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </ModalContainer>
    );
}
